import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { RouterModule } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../../data.service';

interface Dok {
  id: string; tipus: string; tipus_nev: string; cim: string; fajlnev: string | null; meret: number | null;
  hatalyos_tol: string | null; feltoltve: string | null; url: string; feltoltotte: string | null;
}
interface Hely { tipus: string; tipus_nev: string; dokumentum: Dok | null; }
interface Urlap { cim: string; hatalyos_tol: string; fajl: File | null; }

/**
 *  Admin → Adatkezelési szabályzat (backend/adatkezelesi-tajekoztato.js), Nkt. 43. § (1).
 *
 *  Az intézmény adatkezelési szabályzata és a szülőknek/tanulóknak szóló adatkezelési tájékoztató, PDF-ben.
 *  A feltöltött dokumentum azonnal megjelenik a nyilvános /adatkezeles oldalon (a belépő oldal láblécéből,
 *  a családi belépésből és a menü aljáról linkelve). Jog: admin „adatvedelem” terület.
 */
@Component({
  selector: 'app-admin-adatkezelesi-szabalyzat',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, RouterModule],
  templateUrl: './adatkezelesi-szabalyzat.component.html',
  styleUrls: ['./adatkezelesi-szabalyzat.component.scss'],
})
export class AdminAdatkezelesiSzabalyzatComponent implements OnInit {
  intezmeny = '';
  hiba = '';
  fut = false;
  helyek: Hely[] = [];
  urlapok: Record<string, Urlap> = {};
  ment = '';

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    this.intezmeny = String((await this.data.getFromStorage('sajatIntezmeny'))?.intezmenyId || '').replace(/^\/+|\/+$/g, '');
    await this.betolt();
  }

  async betolt() {
    if (!this.intezmeny) { this.hiba = 'Nincs kiválasztott intézmény.'; return; }
    this.fut = true; this.hiba = '';
    try {
      const r = await firstValueFrom(this.http.get<{ helyek: Hely[] }>(`/api/adatkezeles/admin/${this.intezmeny}`));
      this.helyek = Array.isArray(r?.helyek) ? r.helyek : [];
      for (const h of this.helyek) {
        this.urlapok[h.tipus] = { cim: h.dokumentum?.cim || h.tipus_nev, hatalyos_tol: h.dokumentum?.hatalyos_tol || '', fajl: null };
      }
    } catch (e: any) {
      this.hiba = e?.error?.error || 'A dokumentumok nem tölthetők be.';
    } finally {
      this.fut = false;
    }
  }

  fajlValaszt(tipus: string, ev: Event) {
    const f = (ev.target as HTMLInputElement)?.files?.[0] || null;
    if (this.urlapok[tipus]) this.urlapok[tipus].fajl = f;
  }

  async feltolt(h: Hely) {
    const u = this.urlapok[h.tipus];
    if (!u?.fajl) { this.data.sendToast('Válaszd ki a PDF-fájlt.', 'warning'); return; }
    if (!/\.pdf$/i.test(u.fajl.name)) { this.data.sendToast('Csak PDF tölthető fel.', 'warning'); return; }
    const fd = new FormData();
    fd.append('file', u.fajl, u.fajl.name);
    fd.append('cim', String(u.cim || '').trim());
    if (/^\d{4}-\d{2}-\d{2}$/.test(u.hatalyos_tol)) fd.append('hatalyos_tol', u.hatalyos_tol);
    this.ment = h.tipus;
    try {
      await firstValueFrom(this.http.post(`/api/adatkezeles/admin/${h.tipus}/${this.intezmeny}`, fd));
      this.data.sendToast(`${h.tipus_nev}: feltöltve, a nyilvános oldalon már látszik.`, 'success');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A feltöltés nem sikerült.', 'error');
    } finally {
      this.ment = '';
    }
  }

  async torol(h: Hely) {
    if (!h.dokumentum) return;
    if (!confirm(`Biztosan törlöd: ${h.dokumentum.cim}? A nyilvános oldalról is eltűnik.`)) return;
    this.ment = h.tipus;
    try {
      await firstValueFrom(this.http.delete(`/api/adatkezeles/admin/${h.tipus}/${this.intezmeny}`));
      this.data.sendToast('Törölve.', 'success');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A törlés nem sikerült.', 'error');
    } finally {
      this.ment = '';
    }
  }

  /** 'YYYY-MM-DD' → 'YYYY. MM. DD.' */
  datum(d: string | null | undefined): string {
    return /^\d{4}-\d{2}-\d{2}$/.test(String(d || '')) ? `${d!.slice(0, 4)}. ${d!.slice(5, 7)}. ${d!.slice(8, 10)}.` : '–';
  }
}
